import { Link } from 'react-router-dom';
import ContentRenderer from '../renderer/ContentRenderer';
import { plainText } from '../renderer/Inlines';
import { formatDate, typeInfo } from '../contentTypes';
import { INSIGHTS_PATH } from '../../siteConfig';

// Layout around the content body (spec sections 33-34). The document describes content;
// this template decides title, meta line, table of contents, etc.
export default function BlogTemplate({ entry, doc }) {
  const meta = doc.meta || {};
  const blocks = doc.content || [];
  const headings = blocks.filter((b) => b.type === 'heading');
  const minLevel = headings.length ? Math.min(...headings.map((h) => h.level)) : 2;
  const headingOffset = Math.max(0, 2 - minLevel); // page title owns <h1>
  const toc = headings.filter((h) => h.id && h.level <= minLevel + 1);

  // Hide the dek when the excerpt was auto-derived from the first paragraph.
  const firstPara = blocks.find((b) => b.type === 'paragraph');
  const excerpt = (meta.excerpt || '').replace(/\u2026$/, '');
  const showDek = excerpt && !(firstPara && plainText(firstPara.children).startsWith(excerpt));
  const date = formatDate(entry?.sourceModified || entry?.importedAt);

  return (
    <article className="post">
      <header className="post-header">
        <nav className="breadcrumb" aria-label="Breadcrumb">
          <Link to={INSIGHTS_PATH}>All posts</Link>
          <span aria-hidden="true">/</span>
          <span>{typeInfo(doc.type).label}</span>
        </nav>
        {meta.categoryName && <p className="eyebrow">{meta.categoryName}</p>}
        <h1 className="post-title">{meta.title}</h1>
        {showDek && <p className="post-dek">{meta.excerpt}</p>}
        <p className="post-meta">
          {[meta.authorName, date, entry?.readingTimeMinutes && `${entry.readingTimeMinutes} min read`]
            .filter(Boolean)
            .join(' \u00b7 ')}
        </p>
        {meta.tags?.length > 0 && (
          <ul className="tag-list">
            {meta.tags.map((tag) => (
              <li key={tag}>
                <Link className="tag" to={`${INSIGHTS_PATH}?tag=${encodeURIComponent(tag)}`}>{tag}</Link>
              </li>
            ))}
          </ul>
        )}
      </header>

      <div className={`post-layout${toc.length >= 3 ? ' has-toc' : ''}`}>
        <ContentRenderer blocks={blocks} headingOffset={headingOffset} />
        {toc.length >= 3 && (
          <aside className="post-toc" aria-label="On this page">
            <p className="toc-title">On this page</p>
            <ul>
              {toc.map((h) => (
                <li key={h.id} className={h.level > minLevel ? 'sub' : undefined}>
                  <a href={`#${h.id}`}>{plainText(h.children)}</a>
                </li>
              ))}
            </ul>
          </aside>
        )}
      </div>
    </article>
  );
}
